import React, { useState, useRef, useCallback, useEffect, type ReactNode } from "react";
import { Button, Modal, ModalContent, ModalBody, ModalFooter, Snippet } from "@heroui/react";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import useUser from "@/hooks/useUser";
import { MdBugReport } from "react-icons/md";
import { IoClose, IoReload } from "react-icons/io5";
import { LuCopyPlus } from "react-icons/lu";
import { GiProcessor } from "react-icons/gi";
import { isProduction } from "@/utils/environment";

interface DebugPanelProps {
  orderId: string;
  dataForm?: any;
  onOpenChange?: (isOpen: boolean) => void;
}

// Helper function to extract customer dict info
const extractCustomerDictInfo = (fullTextContent: string) => {
  const startMarker = 'dict_info_processed:';
  const endMarker = 'CONSOLIDATION customer ID:';

  const startIndex = fullTextContent.indexOf(startMarker);
  if (startIndex === -1) return null;

  const endIndex = fullTextContent.indexOf(endMarker, startIndex);
  if (endIndex === -1) return null;

  const section = fullTextContent.substring(startIndex + startMarker.length, endIndex);

  // Find JSON object by counting braces
  const openBrace = section.indexOf('{');
  if (openBrace === -1) return null;

  let braceCount = 0;
  let closeBrace = -1;

  for (let i = openBrace; i < section.length; i++) {
    if (section[i] === '{') braceCount++;
    if (section[i] === '}') braceCount--;
    if (braceCount === 0) {
      closeBrace = i;
      break;
    }
  }

  if (closeBrace === -1) return null;

  const jsonString = section.substring(openBrace, closeBrace + 1);

  try {
    const dictInfo = JSON.parse(jsonString.trim());
    return dictInfo;
  } catch (e) {
    console.error('Error parsing dict_info:', e);
    return null;
  }
};

// Helper function to parse table data
const parseTableData = (tableContent: string) => {
  const lines = tableContent.split('\n').filter(line =>
    line.trim() &&
    !line.includes('---') &&
    line.includes('|')
  );
  return lines;
};

// Helper function to extract customer consolidation
const extractCustomerConsolidation = (fullTextContent: string) => {
  const consolidationRegex = /CONSOLIDATION customer ID:\s*\n([\s\S]*?)(?:\n\n\n|DETAILS customer ID ALGO:)/;
  const consolidationMatch = consolidationRegex.exec(fullTextContent);

  if (!consolidationMatch) return null;

  const lines = parseTableData(consolidationMatch[1]);

  if (lines.length === 0) return null;

  const dataParts = lines[1]?.split('|').map(p => p.trim()).filter(Boolean);

  if (!dataParts || dataParts.length < 10) return null;

  const consolidation = {
    id: dataParts[0],
    id_algo: dataParts[1],
    id_hitl: dataParts[2],
    id_final: dataParts[3],
    src_algo: dataParts[4],
    src_hitl: dataParts[5],
    src_final: dataParts[6],
    conf_algo: dataParts[7],
    conf_hitl: dataParts[8],
    conf_final: dataParts[9],
  };

  return consolidation;
};

// Helper function to extract product consolidation
const extractProductConsolidation = (fullTextContent: string, pattern: RegExp) => {
  const productMatch = pattern.exec(fullTextContent);

  if (!productMatch) return [];

  const lines = parseTableData(productMatch[1]);
  const products: any[] = [];
  let headers: string[] = [];

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index];
    const parts = line.split('|').map(p => p.trim()).filter(Boolean);

    if (index === 0) {
      headers = parts;
      continue;
    }

    if (parts.length >= 10) {
      const product: any = {};
      for (let idx = 0; idx < headers.length; idx++) {
        const header = headers[idx];
        if (parts[idx]) {
          product[header.toLowerCase()] = parts[idx];
        }
      }
      products.push(product);
    }
  }

  return products;
};

export default function DebugPanel({ orderId, dataForm, onOpenChange }: Readonly<DebugPanelProps>): React.ReactElement {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { data: userData } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(30); // Percentage of viewport height
  const [isResizing, setIsResizing] = useState(false);
  const [selectedTab, setSelectedTab] = useState("general");
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const [customerData, setCustomerData] = useState<any>(null);
  const [productData, setProductData] = useState<any[]>([]);
  const [productIdData, setProductIdData] = useState<any[]>([]);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: ReactNode } | null>(null);
  const [confirmAction, setConfirmAction] = useState<'emailReprocess' | 'completeReprocess' | 'partialReprocess' | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Show message temporarily
  const showMessage = (type: 'success' | 'error', text: ReactNode, duration = 3000) => {
    setActionMessage({ type, text });
    setTimeout(() => {
      setActionMessage(null);
    }, duration);
  };

  // Parse HTML content to extract structured data
  const parseHtmlContent = (html: string) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Get all text content from pre tags
      const preElements = doc.querySelectorAll('pre');
      let fullTextContent = '';
      for (const pre of Array.from(preElements)) {
        fullTextContent += pre.textContent || '';
      }

      // Extract customer info
      const dictInfo = extractCustomerDictInfo(fullTextContent);
      const consolidation = extractCustomerConsolidation(fullTextContent);

      if (dictInfo || consolidation) {
        setCustomerData({ dictInfo, consolidation });
      }

      // Extract product ID consolidation
      const productIdRegex = /CONSOLIDATION PRODUCT ID:\s*\n([\s\S]*?)(?:\n\n\n|DETAILS PRODUCT ID ALGO:)/;
      const products = extractProductConsolidation(fullTextContent, productIdRegex);
      setProductIdData(products);

      // Extract product quantity consolidation
      const productQtyRegex = /CONSOLIDATION:\s*\n([\s\S]*?)(?:\n\n\n|DETAILS PRODUCT QTY ALGO:)/;
      const qtyProducts = extractProductConsolidation(fullTextContent, productQtyRegex);
      setProductData(qtyProducts);
    } catch (error) {
      console.error('Error in parseHtmlContent:', error);
    }
  };

  const copyToDev = async (): Promise<void> => {
    setLoadingAction('copyToDev');
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step=copy_to_dev`);
      const devUrl = `https://dev-orders.vistaflow.ai/orders/${orderId}`;
      showMessage('success', (
        <span>
          {t("copy_to_dev_success") || "Order copied to dev successfully!"}{" "}
          <a href={devUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
            {devUrl}
          </a>
        </span>
      ), 10000);
    } catch (error) {
      console.error(t("copy_to_dev_error"), error);
      showMessage('error', t("copy_to_dev_error") || "Error copying order to dev");
    } finally {
      setLoadingAction(null);
    }
  };

  const copyToDevParent = async (): Promise<void> => {
    const parentJobId = dataForm?.process?.jobID_parent;
    if (!parentJobId) return;

    setLoadingAction('copyToDevParent');
    try {
      await axiosPrivate.get(`/orders/${parentJobId}/reprocess?reprocessing_step=copy_to_dev`);
      const devUrl = `https://dev-orders.vistaflow.ai/orders/${parentJobId}`;
      showMessage('success', (
        <span>
          {t("copy_to_dev_parent_success") || "Parent order copied to dev successfully!"}{" "}
          <a href={devUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-white">
            {devUrl}
          </a>
        </span>
      ), 10000);
    } catch (error) {
      console.error(t("copy_to_dev_error"), error);
      showMessage('error', t("copy_to_dev_error") || "Error copying parent order to dev");
    } finally {
      setLoadingAction(null);
    }
  };

  const executeCompleteReprocess = async (): Promise<void> => {
    setLoadingAction('completeReprocess');
    setConfirmAction(null);
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step`);
      showMessage('success', t("complete_reprocess_success") || "Complete reprocess started successfully!");
    } catch (error) {
      console.error(t("complete_reprocess_error"), error);
      showMessage('error', t("complete_reprocess_error") || "Error starting complete reprocess");
    } finally {
      setLoadingAction(null);
    }
  };

  const executePartialReprocess = async (): Promise<void> => {
    setLoadingAction('partialReprocess');
    setConfirmAction(null);
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step=skip_llm`);
      showMessage('success', t("partial_reprocess_success") || "Partial reprocess started successfully!");
    } catch (error) {
      console.error(t("partial_reprocess_error"), error);
      showMessage('error', t("partial_reprocess_error") || "Error starting partial reprocess");
    } finally {
      setLoadingAction(null);
    }
  };

  const executeEmailReprocess = async (): Promise<void> => {
    setLoadingAction('emailReprocess');
    setConfirmAction(null);
    try {
      await axiosPrivate.get(`/orders/${orderId}/reprocess?reprocessing_step=from_email_reception`);
      showMessage('success', t("email_reprocess_success") || "Email reprocess started successfully!");
    } catch (error) {
      console.error(t("email_reprocess_error"), error);
      showMessage('error', t("email_reprocess_error") || "Error starting email reprocess");
    } finally {
      setLoadingAction(null);
    }
  };

  const handleConfirmReprocess = () => {
    if (confirmAction === 'emailReprocess') {
      executeEmailReprocess();
    } else if (confirmAction === 'completeReprocess') {
      executeCompleteReprocess();
    } else if (confirmAction === 'partialReprocess') {
      executePartialReprocess();
    }
  };

  const handleEmailReprocessClick = () => {
    if (isProduction) {
      setConfirmAction('emailReprocess');
    } else {
      executeEmailReprocess();
    }
  };

  const handleCompleteReprocessClick = () => {
    if (isProduction) {
      setConfirmAction('completeReprocess');
    } else {
      executeCompleteReprocess();
    }
  };

  const handlePartialReprocessClick = () => {
    if (isProduction) {
      setConfirmAction('partialReprocess');
    } else {
      executePartialReprocess();
    }
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const windowHeight = window.innerHeight;
      const newHeight = ((windowHeight - e.clientY) / windowHeight) * 100;

      // Limit height between 10% and 80%
      if (newHeight >= 10 && newHeight <= 80) {
        setHeight(newHeight);
      }
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Load HTML diagnostic content
  useEffect(() => {
    const loadHtmlDiagnostic = async () => {
      if (!dataForm?.process?.analysis) return;

      setIsLoadingHtml(true);
      try {
        // Try to get html_diag_ALL first, fallback to html_diag_QTY
        const htmlDiagUrl = dataForm.process.analysis.html_diag_ALL_presigned
          || dataForm.process.analysis.html_diag_QTY_presigned
          || dataForm.process.analysis.html_diag_ALL
          || dataForm.process.analysis.html_diag_QTY;

        if (htmlDiagUrl) {
          // Check if it's a URL or HTML content
          const isUrl = htmlDiagUrl.startsWith('http://') || htmlDiagUrl.startsWith('https://');

          if (isUrl) {
            // Fetch the content from the URL
            const response = await fetch(htmlDiagUrl);
            const content = await response.text();
            parseHtmlContent(content);
          } else {
            // It's already HTML content
            parseHtmlContent(htmlDiagUrl);
          }
        }
      } catch (error) {
        console.error("Error loading HTML diagnostic:", error);
      } finally {
        setIsLoadingHtml(false);
      }
    };

    loadHtmlDiagnostic();
  }, [dataForm]);

  const getConfidenceClass = (value: string) => {
    return Number.parseInt(value || '0') >= 75
      ? 'bg-green-900/30 text-green-400'
      : 'bg-orange-900/30 text-orange-400';
  };

  const renderLoadingOrNoData = (isLoading: boolean, hasData: boolean, noDataMessage: string) => {
    if (isLoading) {
      return <p className="text-xs text-gray-500">Loading diagnostic data...</p>;
    }
    if (!hasData) {
      return <p className="text-xs text-gray-500">{noDataMessage}</p>;
    }
    return null;
  };

  return (
    <>
      {/* Toggle Button - Fixed at bottom left */}
      <Button
        isIconOnly
        onPress={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 left-4 z-50 bg-gray-800 text-white shadow-lg hover:bg-gray-700"
        size="lg"
        radius="full"
      >
        {isOpen ? <IoClose size={24} /> : <MdBugReport size={24} />}
      </Button>

      {/* Debug Panel */}
      <div
        ref={panelRef}
        className={`fixed left-0 right-0 bottom-0 bg-[#1a1a1a] border-t border-gray-700 shadow-2xl transition-transform duration-300 ease-in-out z-40 ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ height: `${height}vh` }}
      >
        {/* Resize Handle */}
        <button
          type="button"
          onMouseDown={handleMouseDown}
          className="absolute top-0 left-0 right-0 h-1 cursor-ns-resize bg-gray-700 hover:bg-gray-600 transition-colors border-0 p-0 w-full"
          aria-label="Resize panel"
        >
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-600"></div>
        </button>

        {/* Panel Content */}
        <div className="h-full flex pt-2">
          {/* Header - Fixed at top */}
          <div className="absolute top-2 left-4 right-4 flex items-center justify-between px-2 py-1 z-10">
            <div className="flex items-center gap-2">
              <MdBugReport size={16} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-400">Debug Panel</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Order:</span>
                <Snippet size="sm" variant="flat" hideSymbol classNames={{ base: "bg-transparent p-0", pre: "text-xs text-gray-500" }}>
                  {orderId}
                </Snippet>
                {dataForm?.process?.jobID_parent && dataForm.process.jobID_parent !== dataForm.process.jobID && (
                  <>
                    <span className="text-xs text-gray-500">| Parent:</span>
                    <Snippet size="sm" variant="flat" hideSymbol classNames={{ base: "bg-transparent p-0", pre: "text-xs text-gray-500" }}>
                      {dataForm.process.jobID_parent}
                    </Snippet>
                  </>
                )}
              </div>
              {dataForm?.process?.analysis?.html_diag_QTY && (
                <a
                  href={dataForm.process.analysis.html_diag_QTY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-vistagreen hover:text-vistagreen/80 underline"
                >
                  HTML Diag
                </a>
              )}
            </div>
          </div>

          {/* Left Sidebar - Tabs */}
          <div className="w-48 bg-[#0f0f0f] border-r border-gray-800 flex flex-col pt-10">
            <div className="flex flex-col">
              <button
                onClick={() => setSelectedTab("general")}
                className={`px-4 py-2.5 text-left text-xs transition-colors ${
                  selectedTab === "general"
                    ? "bg-gray-800 text-white border-l-2 border-vistagreen"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-gray-300"
                }`}
              >
                General
              </button>
              <button
                onClick={() => setSelectedTab("infos")}
                className={`px-4 py-2.5 text-left text-xs transition-colors ${
                  selectedTab === "infos"
                    ? "bg-gray-800 text-white border-l-2 border-vistagreen"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-gray-300"
                }`}
              >
                Infos
              </button>
              <button
                onClick={() => setSelectedTab("productId")}
                className={`px-4 py-2.5 text-left text-xs transition-colors ${
                  selectedTab === "productId"
                    ? "bg-gray-800 text-white border-l-2 border-vistagreen"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-gray-300"
                }`}
              >
                Product ID
              </button>
              <button
                onClick={() => setSelectedTab("produits")}
                className={`px-4 py-2.5 text-left text-xs transition-colors ${
                  selectedTab === "produits"
                    ? "bg-gray-800 text-white border-l-2 border-vistagreen"
                    : "text-gray-400 hover:bg-gray-800/50 hover:text-gray-300"
                }`}
              >
                Product QTY
              </button>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 overflow-auto pt-10 px-4">
            {selectedTab === "general" && (
              <div className="space-y-3">
                <div className="bg-[#0f0f0f] p-3 rounded border border-gray-800">
                  <h3 className="text-xs font-semibold mb-2 text-gray-300">Actions</h3>

                  {/* Message display */}
                  {actionMessage && (
                    <div className={`mb-2 px-3 py-2 rounded text-xs ${
                      actionMessage.type === 'success'
                        ? 'bg-vistagreen/20 text-vistagreen border border-vistagreen'
                        : 'bg-red-900/30 text-red-400 border border-red-700'
                    }`}>
                      {actionMessage.text}
                    </div>
                  )}

                  <div className="flex flex-col gap-2 items-start">
                    {userData?.user_email?.includes("@vistaflow.ai") && (
                      <>
                        <Button
                          onPress={handleEmailReprocessClick}
                          size="sm"
                          className="bg-vistagreen hover:bg-vistagreen/90 text-white text-xs h-8"
                          startContent={<IoReload size={14} />}
                          isLoading={loadingAction === 'emailReprocess'}
                          isDisabled={loadingAction !== null}
                        >
                          {t("reprocess_from_email") || "Email Reprocess"}
                        </Button>
                        <Button
                          onPress={handleCompleteReprocessClick}
                          size="sm"
                          className="bg-vistagreen hover:bg-vistagreen/90 text-white text-xs h-8"
                          startContent={<GiProcessor size={14} />}
                          isLoading={loadingAction === 'completeReprocess'}
                          isDisabled={loadingAction !== null}
                        >
                          {t("complete_reprocess") || "Complete Reprocess"}
                        </Button>
                        <Button
                          onPress={handlePartialReprocessClick}
                          size="sm"
                          className="bg-vistagreen hover:bg-vistagreen/90 text-white text-xs h-8"
                          startContent={<IoReload size={14} />}
                          isLoading={loadingAction === 'partialReprocess'}
                          isDisabled={loadingAction !== null}
                        >
                          {t("post_llm_reprocess") || "Partial Reprocess"}
                        </Button>
                      </>
                    )}
                    <Button
                      onPress={copyToDev}
                      size="sm"
                      className="bg-vistagreen hover:bg-vistagreen/90 text-white text-xs h-8"
                      startContent={<LuCopyPlus size={14} />}
                      isLoading={loadingAction === 'copyToDev'}
                      isDisabled={loadingAction !== null}
                    >
                      {t("copy_to_dev") || "Copy to Dev"}
                    </Button>
                    {dataForm?.process?.jobID_parent && dataForm.process.jobID_parent !== dataForm.process.jobID && (
                      <Button
                        onPress={copyToDevParent}
                        size="sm"
                        className="bg-vistagreen hover:bg-vistagreen/90 text-white text-xs h-8"
                        startContent={<LuCopyPlus size={14} />}
                        isLoading={loadingAction === 'copyToDevParent'}
                        isDisabled={loadingAction !== null}
                      >
                        {t("copy_to_dev_parent") || "Copy to Dev Parent"}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedTab === "infos" && (
              <div className="space-y-3 w-full">
                {renderLoadingOrNoData(isLoadingHtml, !!customerData, "No diagnostic data available")}
                {!isLoadingHtml && customerData && (
                  <>
                    {/* Customer Info */}
                    {customerData.dictInfo && (
                      <div className="bg-[#0f0f0f] p-3 rounded border border-gray-800">
                        <h3 className="text-xs font-semibold mb-2 text-gray-300">Customer Information</h3>
                        <div className="space-y-1 text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-gray-500">Customer ID:</span>
                              <span className="text-white ml-2 font-mono">{customerData.dictInfo.customer_id}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Customer Name:</span>
                              <span className="text-white ml-2">{customerData.dictInfo.customer_name || 'N/A'}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-gray-500">Email Source:</span>
                              <span className="text-white ml-2 font-mono">{customerData.dictInfo.email_body_source}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Consolidation Table */}
                    {customerData.consolidation && (
                      <div className="bg-[#0f0f0f] p-3 rounded border border-gray-800">
                        <h3 className="text-xs font-semibold mb-2 text-gray-300">Customer ID Consolidation</h3>
                        <div className="overflow-x-auto max-w-full">
                          <table className="text-xs border-collapse min-w-max">
                            <thead>
                              <tr className="border-b border-gray-700">
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_ALGO</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_HITL</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_FINAL</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">SRC_ALGO</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">SRC_HITL</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">SRC_FINAL</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_ALGO</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL</th>
                                <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_FINAL</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="border-b border-gray-800 hover:bg-gray-800/30">
                                <td className="p-1.5 text-white font-mono whitespace-nowrap">{customerData.consolidation.id}</td>
                                <td className="p-1.5 text-blue-400 font-mono whitespace-nowrap">{customerData.consolidation.id_algo}</td>
                                <td className="p-1.5 text-gray-300 font-mono whitespace-nowrap">{customerData.consolidation.id_hitl}</td>
                                <td className="p-1.5 text-green-400 font-mono whitespace-nowrap">{customerData.consolidation.id_final}</td>
                                <td className="p-1.5 text-gray-300 text-xs max-w-xs truncate" title={customerData.consolidation.src_algo}>
                                  {customerData.consolidation.src_algo}
                                </td>
                                <td className="p-1.5 text-gray-300 text-xs whitespace-nowrap">{customerData.consolidation.src_hitl}</td>
                                <td className="p-1.5 text-gray-300 text-xs max-w-xs truncate" title={customerData.consolidation.src_final}>
                                  {customerData.consolidation.src_final}
                                </td>
                                <td className="p-1.5 whitespace-nowrap">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(customerData.consolidation.conf_algo)}`}>
                                    {customerData.consolidation.conf_algo}
                                  </span>
                                </td>
                                <td className="p-1.5 whitespace-nowrap">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(customerData.consolidation.conf_hitl || '0')}`}>
                                    {customerData.consolidation.conf_hitl || '0'}
                                  </span>
                                </td>
                                <td className="p-1.5 whitespace-nowrap">
                                  <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(customerData.consolidation.conf_final)}`}>
                                    {customerData.consolidation.conf_final}
                                  </span>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {selectedTab === "productId" && (
              <div className="space-y-3 w-full">
                {renderLoadingOrNoData(isLoadingHtml, productIdData.length > 0, "No product ID data available")}
                {!isLoadingHtml && productIdData.length > 0 && (
                  <div className="bg-[#0f0f0f] p-3 rounded border border-gray-800">
                    <h3 className="text-xs font-semibold mb-2 text-gray-300">
                      Products ID Consolidation ({productIdData.length} products)
                    </h3>
                    <div className="overflow-x-auto max-w-full">
                      <table className="text-xs border-collapse min-w-max">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left p-1.5 text-gray-400 font-semibold sticky left-0 bg-[#0f0f0f] z-10">#</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold sticky left-6 bg-[#0f0f0f] z-10">ID</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_ALGO</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_HITL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_HITL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_HITL_MANUAL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">ID_FINAL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">SRC_ALGO</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_ALGO</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL_MANUAL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_FINAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productIdData.map((product, idx) => (
                            <tr key={product.id || `product-id-${idx}`} className="border-b border-gray-800 hover:bg-gray-800/30">
                              <td className="p-1.5 text-gray-400 sticky left-0 bg-[#0f0f0f] z-10">{product['-'] || idx}</td>
                              <td className="p-1.5 text-white font-mono sticky left-6 bg-[#0f0f0f] z-10">{product.id}</td>
                              <td className="p-1.5 text-blue-400 font-mono whitespace-nowrap">{product.id_algo}</td>
                              <td className="p-1.5 text-gray-300 font-mono whitespace-nowrap">{product.id_hitl}</td>
                              <td className="p-1.5 text-gray-300 font-mono whitespace-nowrap">{product.id_hitl_prs}</td>
                              <td className="p-1.5 text-gray-300 font-mono whitespace-nowrap">{product.id_hitl_manual_prs}</td>
                              <td className="p-1.5 text-green-400 font-mono font-semibold whitespace-nowrap">{product.id_final}</td>
                              <td className="p-1.5 text-gray-300 text-xs max-w-md truncate" title={product.src_algo}>
                                {product.src_algo}
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_algo || '0')}`}>
                                  {product.conf_algo}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_hitl || '0')}`}>
                                  {product.conf_hitl}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_hitl_prs || '0')}`}>
                                  {product.conf_hitl_prs}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_hitl_manual_prs || '0')}`}>
                                  {product.conf_hitl_manual_prs}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_final || '0')}`}>
                                  {product.conf_final}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedTab === "produits" && (
              <div className="space-y-3 w-full">
                {renderLoadingOrNoData(isLoadingHtml, productData.length > 0, "No product data available")}
                {!isLoadingHtml && productData.length > 0 && (
                  <div className="bg-[#0f0f0f] p-3 rounded border border-gray-800">
                    <h3 className="text-xs font-semibold mb-2 text-gray-300">
                      Products Quantity Consolidation ({productData.length} products)
                    </h3>
                    <div className="overflow-x-auto max-w-full">
                      <table className="text-xs border-collapse min-w-max">
                        <thead>
                          <tr className="border-b border-gray-700">
                            <th className="text-left p-1.5 text-gray-400 font-semibold sticky left-0 bg-[#0f0f0f] z-10">#</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold sticky left-10 bg-[#0f0f0f] z-10">ID</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">PCK</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY_PCK</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY_PRX</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY_HITL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY_HITL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY_HITL_MANUAL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">QTY_FINAL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_PCK</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_PRX</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_HITL_MANUAL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">CONF_FINAL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">MUL_PCK</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">MUL_PRX</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">MUL_HITL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">MUL_HITL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">MUL_HITL_MANUAL_PRS</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">MUL_FINAL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">PCK_FINAL</th>
                            <th className="text-left p-1.5 text-gray-400 font-semibold whitespace-nowrap">PCK_CUSTOMER_FINAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productData.map((product, idx) => (
                            <tr key={product.id || `product-qty-${idx}`} className="border-b border-gray-800 hover:bg-gray-800/30">
                              <td className="p-1.5 text-gray-400 sticky left-0 bg-[#0f0f0f] z-10">{product['-'] || idx}</td>
                              <td className="p-1.5 text-white font-mono sticky left-6 bg-[#0f0f0f] z-10">{product.id}</td>
                              <td className="p-1.5 text-blue-400 whitespace-nowrap">{product.qty}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.pck}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.qty_pck}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.qty_prx}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.qty_hitl}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.qty_hitl_prs}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.qty_hitl_manual_prs}</td>
                              <td className="p-1.5 text-green-400 font-semibold whitespace-nowrap">{product.qty_final}</td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_pck || '0')}`}>
                                  {product.conf_pck}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_prx || '0')}`}>
                                  {product.conf_prx}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_hitl || '0')}`}>
                                  {product.conf_hitl}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_hitl_prs || '0')}`}>
                                  {product.conf_hitl_prs}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_hitl_manual_prs || '0')}`}>
                                  {product.conf_hitl_manual_prs}
                                </span>
                              </td>
                              <td className="p-1.5 whitespace-nowrap">
                                <span className={`px-1.5 py-0.5 rounded text-xs ${getConfidenceClass(product.conf_final || '0')}`}>
                                  {product.conf_final}
                                </span>
                              </td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.mul_pck}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.mul_prx}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.mul_hitl}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.mul_hitl_prs}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.mul_hitl_manual_prs}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.mul_final}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.pck_final}</td>
                              <td className="p-1.5 text-gray-300 whitespace-nowrap">{product.pck_csutomer_final}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        backdrop="opaque"
        placement="center"
        size="md"
        classNames={{
          backdrop: "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
        }}
      >
        <ModalContent>
          <ModalBody>
            <div className="pt-5 pb-3">
              <div className="text-xl font-bold pb-3">{t("confirm_reprocess") || "Confirm Reprocess"}</div>
              <div className="text-red-500 font-bold text-sm">
                {t("confirm_reprocess_message") || "Do you want to launch the reprocess?"}
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="flat"
              onPress={() => setConfirmAction(null)}
              className="capitalize"
            >
              {t("no") || "No"}
            </Button>
            <Button
              className="bg-vistagreen hover:bg-vistagreen/90 text-white capitalize"
              onPress={handleConfirmReprocess}
            >
              {t("yes") || "Yes"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
